import React, { useCallback, useEffect, useState } from 'react';
import {
    AlertTriangle,
    BadgeDollarSign,
    Brain,
    CheckCircle2,
    Cpu,
    DownloadCloud,
    Eye,
    EyeOff,
    KeyRound,
    Loader2,
    RefreshCw,
    Save,
    XCircle,
    Zap,
} from 'lucide-react';
import type {
    CatalogoPrecos,
    ConfigIa,
    CustoPorSite,
    DiagnosticoIa,
    ListaModelos,
    ResumoCustoIa,
} from '@nyroh/contracts';

import { api, ErroApiNyroh } from '../lib/api';

/**
 * ============================================================================
 * INTELIGÊNCIA ARTIFICIAL — a chave, o modelo e o que cada site custou
 * ============================================================================
 *
 * Esta tela responde quatro perguntas que a fábrica não tinha onde fazer:
 *
 *   1. A chave do Gemini está funcionando?  -> botão TESTAR, que GERA de verdade
 *   2. Quais modelos existem e quanto custam? -> catálogo ao vivo do provedor
 *   3. Qual modelo a plataforma vai usar?     -> escolha gravada no banco
 *   4. Quanto cada site custou?               -> tokens reais, não estimativa
 *
 * NADA AQUI GUARDA CHAVE NO NAVEGADOR. O campo de chave é enviado uma vez para
 * `ia.salvarConfig`, que a cifra com AES-256-GCM antes de encostar no banco. O
 * que volta são os quatro últimos caracteres — o suficiente para responder "é a
 * que eu colei?" e insuficiente para reconstruir qualquer coisa.
 */

type Periodo = 'hoje' | '7d' | '30d' | 'todos';

const ROTULO_PERIODO: Record<Periodo, string> = {
    hoje: 'Hoje',
    '7d': 'Últimos 7d',
    '30d': 'Últimos 30d',
    todos: 'Tudo',
};

const brl = (v: number | null) =>
    v === null ? '—' : `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;

const usd = (v: number | null) => (v === null ? '—' : `US$ ${v.toFixed(6)}`);

const numero = (v: number) => v.toLocaleString('pt-BR');

/** Quantos dias faz que este preço foi conferido. Preço velho é pergunta, não fato. */
function diasDesde(iso: string): number {
    const d = new Date(`${iso}T00:00:00.000Z`).getTime();
    return Math.max(0, Math.floor((Date.now() - d) / 86_400_000));
}

function mensagemDeErro(e: unknown): string {
    if (e instanceof ErroApiNyroh) return e.message;
    return e instanceof Error ? e.message : String(e);
}

export const IaControlView: React.FC = () => {
    const [config, setConfig] = useState<ConfigIa | null>(null);
    const [modelos, setModelos] = useState<ListaModelos | null>(null);
    const [resumo, setResumo] = useState<ResumoCustoIa | null>(null);
    const [porSite, setPorSite] = useState<CustoPorSite[]>([]);
    const [periodo, setPeriodo] = useState<Periodo>('30d');

    const [chave, setChave] = useState('');
    const [mostrarChave, setMostrarChave] = useState(false);
    const [diagnostico, setDiagnostico] = useState<DiagnosticoIa | null>(null);

    /*
      O editor de preço, aberto por modelo.

      Ele existe porque a tela DIZ "cadastre o preço deste modelo" em três
      lugares — no aviso do resumo, na coluna do catálogo e no diagnóstico. Sem
      um lugar para cadastrar, a instrução é um beco: a rota `ia.salvarPreco`
      existia e nenhuma interface a chamava.
    */
    const [editandoPreco, setEditandoPreco] = useState<string | null>(null);
    const [entradaMi, setEntradaMi] = useState('');
    const [saidaMi, setSaidaMi] = useState('');

    /*
      O catálogo de preços do Google.

      Consultado NO CLIQUE, nunca no carregamento: são dois GETs no catálogo
      inteiro do Cloud Billing, e abrir esta tela não pode custar duas idas ao
      Google que ninguém pediu.
    */
    const [catalogo, setCatalogo] = useState<CatalogoPrecos | null>(null);
    const [buscandoPrecos, setBuscandoPrecos] = useState(false);
    const [marcados, setMarcados] = useState<Set<string>>(new Set());
    const [verIgnorados, setVerIgnorados] = useState(false);

    const [carregando, setCarregando] = useState(true);
    const [testando, setTestando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [aviso, setAviso] = useState<string | null>(null);

    const carregarConfig = useCallback(async () => {
        const [c, m] = await Promise.all([api.chamar('ia.config', {}), api.chamar('ia.modelos', {})]);
        setConfig(c);
        setModelos(m);
    }, []);

    const carregarCusto = useCallback(async (p: Periodo) => {
        const [r, s] = await Promise.all([
            api.chamar('ia.resumoCusto', { query: { periodo: p } }),
            api.chamar('ia.custoPorSite', { query: { periodo: p, limite: 50 } }),
        ]);
        setResumo(r);
        setPorSite(s);
    }, []);

    /*
      DOIS efeitos, e a separação importa.

      `carregarConfig` faz uma consulta AO VIVO ao provedor (`ia.modelos`). Com
      `periodo` na lista de dependências de um efeito só, cada clique no filtro
      do relatório disparava uma chamada externa — e um soluço do provedor
      apagava a tabela de modelos da tela sem que ninguém tivesse mexido nela.
    */
    useEffect(() => {
        let vivo = true;
        setCarregando(true);
        setErro(null);
        carregarConfig()
            .catch((e) => vivo && setErro(mensagemDeErro(e)))
            .finally(() => vivo && setCarregando(false));
        return () => {
            vivo = false;
        };
    }, [carregarConfig]);

    useEffect(() => {
        let vivo = true;
        carregarCusto(periodo).catch((e) => vivo && setErro(mensagemDeErro(e)));
        return () => {
            vivo = false;
        };
    }, [carregarCusto, periodo]);

    /**
     * Testa a chave digitada, quando há uma, ou a que está gravada.
     *
     * Passar a chave do campo é o que permite CONFERIR ANTES DE SALVAR. Sem
     * isso, descobrir que a chave está errada exigiria gravá-la por cima da que
     * funcionava — e não haveria como voltar.
     */
    const testar = async () => {
        setTestando(true);
        setErro(null);
        setDiagnostico(null);
        try {
            const d = await api.chamar('ia.diagnosticar', {
                corpo: chave.trim() ? { chave: chave.trim() } : {},
            });
            setDiagnostico(d);
        } catch (e) {
            setErro(mensagemDeErro(e));
        } finally {
            setTestando(false);
        }
    };

    const salvar = async (dados: Parameters<typeof api.chamar<'ia.salvarConfig'>>[1]['corpo']) => {
        setSalvando(true);
        setErro(null);
        setAviso(null);
        try {
            const c = await api.chamar('ia.salvarConfig', { corpo: dados });
            setConfig(c);
            setChave('');
            setAviso('Configuração salva.');
            // O catálogo depende da chave: trocar a chave pode mudar quais
            // modelos aparecem, e a lista antiga viraria uma oferta falsa.
            setModelos(await api.chamar('ia.modelos', {}));
        } catch (e) {
            setErro(mensagemDeErro(e));
        } finally {
            setSalvando(false);
        }
    };

    /**
     * Lê o preço publicado pelo Google (Cloud Billing Catalog API).
     *
     * A API do Gemini devolve TOKEN, nunca dinheiro: não existe custo na
     * resposta de `generateContent`. O que volta daqui é PROPOSTA — o catálogo
     * fala em SKU, com descrição em texto e várias entradas por modelo (tier,
     * região, faixa de contexto), então o casamento SKU → modelo é heurístico.
     * Importar em silêncio trocaria "preço digitado errado" por "preço do SKU
     * errado", que parece automático e ninguém confere.
     */
    const buscarPrecos = async () => {
        setBuscandoPrecos(true);
        setErro(null);
        setAviso(null);
        try {
            const c = await api.chamar('ia.precosDoProvedor', {});
            setCatalogo(c);
            setMarcados(
                new Set(c.propostas.filter((x) => x.mudou && x.aplicavel).map((x) => x.modelo)),
            );
            if (c.aviso) setAviso(c.aviso);
        } catch (e) {
            setErro(mensagemDeErro(e));
        } finally {
            setBuscandoPrecos(false);
        }
    };

    const aplicarPrecos = async () => {
        if (marcados.size === 0) return;
        setSalvando(true);
        setErro(null);
        try {
            const r = await api.chamar('ia.aplicarPrecosDoProvedor', {
                corpo: { modelos: [...marcados] },
            });
            setModelos(await api.chamar('ia.modelos', {}));
            setCatalogo(null);
            setMarcados(new Set());
            setAviso(
                `${r.aplicados} preço(s) gravados com a data de hoje.` +
                    (r.naoAplicados.length
                        ? ` ${r.naoAplicados.length} recusado(s): ${r.naoAplicados
                              .map((n) => `${n.modelo} (${n.motivo})`)
                              .join('; ')}`
                        : '') +
                    ' O custo já registrado NÃO é recalculado — vale das próximas chamadas.',
            );
        } catch (e) {
            setErro(mensagemDeErro(e));
        } finally {
            setSalvando(false);
        }
    };

    const salvarPreco = async (modelo: string) => {
        const entrada = parseFloat(entradaMi);
        const saida = parseFloat(saidaMi);
        if (!Number.isFinite(entrada) || !Number.isFinite(saida) || entrada < 0 || saida < 0) {
            setErro('Informe os dois preços em dólar por milhão de tokens, sem sinal negativo.');
            return;
        }
        setSalvando(true);
        setErro(null);
        try {
            await api.chamar('ia.salvarPreco', {
                params: { modelo },
                corpo: {
                    usdEntradaMi: entrada,
                    usdSaidaMi: saida,
                    fonte: 'cadastrado na tela de IA',
                },
            });
            setEditandoPreco(null);
            setModelos(await api.chamar('ia.modelos', {}));
            /*
              O custo NÃO é recalculado para trás.

              As linhas de `uso_ia` já gravadas continuam com `custo = NULL`: elas
              foram medidas quando não havia preço, e reescrevê-las agora seria
              inventar um número para um gasto passado com uma tabela de preço de
              hoje. O aviso de "N chamadas sem preço" continua contando essas —
              corretamente. O preço novo vale das próximas chamadas em diante.
            */
            setAviso(
                `Preço de "${modelo}" cadastrado. Ele vale das próximas chamadas em diante; ` +
                    'as já registradas sem preço continuam sem, porque foram medidas assim.',
            );
        } catch (e) {
            setErro(mensagemDeErro(e));
        } finally {
            setSalvando(false);
        }
    };

    if (carregando && !config) {
        return (
            <div className="p-8 flex items-center gap-3 text-slate-500">
                <Loader2 className="animate-spin" size={18} /> Consultando a configuração de IA…
            </div>
        );
    }

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-8 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-6">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Brain className="text-orange-600 w-7 h-7" />
                        Inteligência Artificial
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Chave, modelo e o custo real de cada site — medido em tokens, não estimado.
                    </p>
                </div>
                <button
                    onClick={() => {
                        setCarregando(true);
                        Promise.all([carregarConfig(), carregarCusto(periodo)])
                            .catch((e) => setErro(mensagemDeErro(e)))
                            .finally(() => setCarregando(false));
                    }}
                    className="bg-white hover:bg-zinc-50 border border-zinc-200 text-slate-800 px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                >
                    <RefreshCw size={14} className={carregando ? 'animate-spin' : ''} /> Atualizar
                </button>
            </div>

            {erro && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-sm p-4 text-sm flex items-start gap-2">
                    <XCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{erro}</span>
                </div>
            )}
            {aviso && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm p-4 text-sm flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                    <span>{aviso}</span>
                </div>
            )}

            {/* ------------------------------------------------------ chave -- */}
            <section className="bg-white border border-zinc-200 rounded-sm p-8 shadow-sm space-y-6">
                <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-zinc-50 rounded-sm border border-zinc-200 flex items-center justify-center shrink-0">
                        <KeyRound className="text-orange-700" size={22} />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900">Chave do Gemini</h3>
                        <p className="text-slate-500 text-sm leading-relaxed mt-1">
                            A chave é gravada <strong>cifrada</strong> no banco e nunca volta para o navegador.
                            Se não houver chave no banco, a API usa a variável <code>GEMINI_API_KEY</code> do
                            ambiente — a queda continua existindo, ela só deixou de ser o único caminho.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm border-t border-zinc-100 pt-6">
                    <div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Em uso</div>
                        <div className="font-semibold text-slate-900">
                            {config?.fonteChave === 'banco' && 'Chave gravada no banco'}
                            {config?.fonteChave === 'ambiente' && 'GEMINI_API_KEY do ambiente'}
                            {config?.fonteChave === 'nenhuma' && (
                                <span className="text-red-700">Nenhuma chave configurada</span>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                            Chave no banco
                        </div>
                        <div className="font-mono text-slate-900">
                            {config?.chaveSufixo ? `••••••••${config.chaveSufixo}` : '— não há —'}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                            Queda para o ambiente
                        </div>
                        <div className="font-semibold text-slate-900">
                            {config?.temChaveNoAmbiente ? 'Disponível' : 'Não configurada'}
                        </div>
                    </div>
                </div>

                {config && !config.podeGuardarChave && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-4 text-xs flex items-start gap-2">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>
                            <strong>Não dá para gravar a chave neste servidor:</strong>{' '}
                            <code>CHAVE_CIFRA_DADOS</code> não está definida na API. Sem ela a chave iria ao
                            banco em texto puro, e isso não vai acontecer. Enquanto isso, use{' '}
                            <code>GEMINI_API_KEY</code> no ambiente da API.
                        </span>
                    </div>
                )}

                <div className="flex flex-col md:flex-row gap-3 border-t border-zinc-100 pt-6">
                    <div className="flex-1 relative">
                        <input
                            type={mostrarChave ? 'text' : 'password'}
                            value={chave}
                            onChange={(e) => setChave(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                            placeholder="Cole aqui a chave (aistudio.google.com/apikey)"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-sm px-4 py-3 pr-11 text-slate-900 font-mono text-sm focus:outline-none focus:border-orange-700"
                        />
                        <button
                            type="button"
                            onClick={() => setMostrarChave((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                            aria-label={mostrarChave ? 'Ocultar a chave' : 'Mostrar a chave'}
                        >
                            {mostrarChave ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                    <button
                        onClick={testar}
                        disabled={testando}
                        title="Faz uma geração de verdade. Chave válida com faturamento desligado passa num teste de autenticação e falha aqui — que é o ponto."
                        className="bg-white border border-slate-300 hover:bg-zinc-50 text-slate-800 px-5 py-3 rounded-sm text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {testando ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                        {chave.trim() ? 'Testar esta chave' : 'Testar a chave em uso'}
                    </button>
                    <button
                        onClick={() => salvar({ chave: chave.trim() })}
                        disabled={salvando || !chave.trim() || !config?.podeGuardarChave}
                        className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-3 rounded-sm text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40"
                    >
                        {salvando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Salvar chave
                    </button>
                </div>

                {config?.chaveSufixo && (
                    <button
                        onClick={() => salvar({ chave: '' })}
                        disabled={salvando}
                        className="text-xs text-slate-500 hover:text-red-700 underline underline-offset-2"
                    >
                        Apagar a chave do banco e voltar a usar a do ambiente
                    </button>
                )}

                {diagnostico && (
                    <div
                        className={`rounded-sm p-4 text-sm border ${
                            diagnostico.ok
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                                : 'bg-red-50 border-red-200 text-red-900'
                        }`}
                    >
                        <div className="flex items-start gap-2 font-semibold">
                            {diagnostico.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                            <span>{diagnostico.mensagem}</span>
                        </div>
                        <div className="mt-2 text-xs opacity-80 space-y-0.5 pl-6">
                            <div>
                                Modelo testado: <strong>{diagnostico.modelo}</strong>
                                {diagnostico.statusHttp > 0 && ` · HTTP ${diagnostico.statusHttp}`}
                                {` · ${diagnostico.ms} ms`}
                            </div>
                            {diagnostico.resposta && <div>O modelo respondeu: “{diagnostico.resposta}”</div>}
                            {diagnostico.consumo && (
                                <div>
                                    Custo deste teste: {diagnostico.consumo.tokensTotal} tokens ·{' '}
                                    {diagnostico.consumo.precoConhecido
                                        ? brl(diagnostico.consumo.custoBrl)
                                        : 'preço deste modelo não cadastrado'}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>

            {/* ---------------------------------------------------- modelos -- */}
            <section className="bg-white border border-zinc-200 rounded-sm p-8 shadow-sm space-y-6">
                <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-zinc-50 rounded-sm border border-zinc-200 flex items-center justify-center shrink-0">
                        <Cpu className="text-orange-700" size={22} />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900">Modelo usado pela plataforma</h3>
                        <p className="text-slate-500 text-sm leading-relaxed mt-1">
                            A lista vem <strong>ao vivo do provedor</strong>, com a chave em uso: é o único jeito
                            honesto de mostrar o que esta conta alcança. Ordenada do mais barato para o mais caro.
                        </p>
                    </div>
                    {/*
                      O preço vem do CATÁLOGO, não da resposta da geração: a API
                      do Gemini devolve token, nunca dinheiro.
                    */}
                    <button
                        onClick={() => void buscarPrecos()}
                        disabled={buscandoPrecos}
                        className="shrink-0 flex items-center gap-2 bg-white border border-zinc-300 hover:border-slate-900 text-slate-900 px-4 py-2 rounded-sm text-sm font-bold disabled:opacity-40"
                    >
                        {buscandoPrecos ? (
                            <Loader2 size={15} className="animate-spin" />
                        ) : (
                            <DownloadCloud size={15} />
                        )}
                        Buscar preços no Google
                    </button>
                </div>

                {/* ------------------------------------ proposta do catálogo -- */}
                {catalogo && (
                    <div className="border border-orange-200 bg-orange-50/40 rounded-sm p-5 space-y-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <h4 className="text-sm font-bold text-slate-900 mr-auto">
                                Preços publicados pelo Google
                                <span className="ml-2 font-normal text-xs text-slate-500">
                                    {catalogo.consultou
                                        ? `${catalogo.propostas.length} modelo(s) casados · chave ${
                                              catalogo.fonteChave === 'faturamento'
                                                  ? 'de faturamento'
                                                  : 'do Gemini'
                                          }`
                                        : 'não consultado'}
                                </span>
                            </h4>
                            {catalogo.propostas.length > 0 && (
                                <button
                                    onClick={() => void aplicarPrecos()}
                                    disabled={salvando || marcados.size === 0}
                                    className="bg-orange-700 hover:bg-orange-800 text-white px-4 py-2 rounded-sm text-sm font-bold disabled:opacity-40"
                                >
                                    Aplicar {marcados.size} preço{marcados.size === 1 ? '' : 's'}
                                </button>
                            )}
                            <button
                                onClick={() => setCatalogo(null)}
                                className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
                            >
                                fechar
                            </button>
                        </div>

                        {catalogo.aviso && (
                            <p className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-3 text-xs leading-relaxed">
                                {catalogo.aviso}
                            </p>
                        )}

                        {catalogo.propostas.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-[10px] text-slate-400 uppercase tracking-wider font-bold border-b border-orange-200">
                                            <th className="pb-2 pr-3">Aplicar</th>
                                            <th className="pb-2 pr-3">Modelo</th>
                                            <th className="pb-2 pr-3 text-right">Cadastrado</th>
                                            <th className="pb-2 pr-3 text-right">Google</th>
                                            <th className="pb-2">SKU usado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-orange-100">
                                        {catalogo.propostas.map((x) => (
                                            <tr key={x.modelo} className={x.mudou ? '' : 'opacity-60'}>
                                                <td className="py-2 pr-3">
                                                    <input
                                                        type="checkbox"
                                                        disabled={!x.aplicavel}
                                                        checked={marcados.has(x.modelo)}
                                                        onChange={(e) => {
                                                            const s = new Set(marcados);
                                                            if (e.target.checked) s.add(x.modelo);
                                                            else s.delete(x.modelo);
                                                            setMarcados(s);
                                                        }}
                                                    />
                                                </td>
                                                <td className="py-2 pr-3 font-mono font-bold text-slate-900">
                                                    {x.modelo}
                                                </td>
                                                <td className="py-2 pr-3 text-right font-mono text-slate-500">
                                                    {x.atual
                                                        ? `${x.atual.usdEntradaMi.toFixed(3)} / ${x.atual.usdSaidaMi.toFixed(3)}`
                                                        : '— sem preço —'}
                                                </td>
                                                <td
                                                    className={`py-2 pr-3 text-right font-mono ${
                                                        x.mudou ? 'font-bold text-orange-700' : 'text-slate-500'
                                                    }`}
                                                >
                                                    {x.usdEntradaMi === null || x.usdSaidaMi === null
                                                        ? 'incompleto'
                                                        : `${x.usdEntradaMi.toFixed(3)} / ${x.usdSaidaMi.toFixed(3)}`}
                                                    {x.usdRaciocinioMi !== null && (
                                                        <span className="block text-[10px] font-normal text-slate-500">
                                                            raciocínio {x.usdRaciocinioMi.toFixed(3)}
                                                        </span>
                                                    )}
                                                </td>
                                                {/*
                                                  A descrição do SKU e o preço CRU ficam à vista: a
                                                  conversão para "por milhão" é a parte frágil, e com
                                                  o valor cru ao lado o erro aparece ANTES de gravar.
                                                */}
                                                <td className="py-2 text-[10px] leading-relaxed text-slate-500">
                                                    {x.skus.map((s) => (
                                                        <div key={`${s.papel}-${s.id}`}>
                                                            <span className="font-bold uppercase">{s.papel}</span>:{' '}
                                                            {s.descricao}{' '}
                                                            <span className="font-mono">
                                                                ({s.precoUnitario} / {s.unidade})
                                                            </span>
                                                            {s.variantes > 1 && (
                                                                <span className="text-amber-700">
                                                                    {' '}
                                                                    · {s.variantes} variantes, usei a mais barata
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {!x.aplicavel && (
                                                        <div className="text-amber-700 font-bold">
                                                            o catálogo trouxe só um lado — não dá para aplicar
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {catalogo.ignorados.length > 0 && (
                            <div>
                                <button
                                    onClick={() => setVerIgnorados(!verIgnorados)}
                                    className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
                                >
                                    {verIgnorados ? 'esconder' : 'ver'} {catalogo.ignorados.length} SKU(s)
                                    descartado(s)
                                </button>
                                {verIgnorados && (
                                    <ul className="mt-2 space-y-1 text-[11px] text-slate-500 max-h-48 overflow-y-auto">
                                        {catalogo.ignorados.map((i, k) => (
                                            <li key={`${i.descricao}-${k}`}>
                                                <span className="font-mono">{i.descricao}</span> — {i.motivo}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        <p className="text-[11px] leading-relaxed text-slate-500 border-t border-orange-200 pt-3">
                            A API do Gemini devolve <strong>token, nunca dinheiro</strong> — não existe custo na
                            resposta da geração. Isto vem da Cloud Billing Catalog API
                            {catalogo.servico && (
                                <>
                                    {' '}
                                    (<code className="font-mono">{catalogo.servico}</code>)
                                </>
                            )}
                            , gravado com a data de hoje e a origem na linha. O que já foi cobrado não é
                            recalculado.
                        </p>
                    </div>
                )}

                {modelos && !modelos.consultouProvedor && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-4 text-sm flex items-start gap-2">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>{modelos.aviso}</span>
                    </div>
                )}

                {modelos && modelos.modelos.length > 0 && (
                    <div className="overflow-x-auto border-t border-zinc-100 pt-6">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest">
                                    <th className="pb-3 pr-4">Modelo</th>
                                    <th className="pb-3 pr-4 text-right">US$ / 1M entrada</th>
                                    <th className="pb-3 pr-4 text-right">US$ / 1M saída</th>
                                    <th className="pb-3 pr-4">Preço conferido</th>
                                    <th className="pb-3 text-right">Usar</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {modelos.modelos.map((m) => (
                                    <tr key={m.id} className={m.emUso ? 'bg-orange-50/60' : ''}>
                                        <td className="py-3 pr-4">
                                            <div className="font-semibold text-slate-900 font-mono text-xs">{m.id}</div>
                                            <div className="text-xs text-slate-500">{m.nome}</div>
                                        </td>
                                        <td className="py-3 pr-4 text-right font-mono text-xs">
                                            {m.preco ? m.preco.usdEntradaMi.toFixed(3) : '—'}
                                        </td>
                                        <td className="py-3 pr-4 text-right font-mono text-xs">
                                            {m.preco ? m.preco.usdSaidaMi.toFixed(3) : '—'}
                                        </td>
                                        <td className="py-3 pr-4 text-xs">
                                            {m.preco ? (
                                                <span
                                                    className={
                                                        diasDesde(m.preco.conferidoEm) > 90
                                                            ? 'text-amber-700 font-semibold'
                                                            : 'text-slate-500'
                                                    }
                                                >
                                                    {m.preco.conferidoEm}
                                                    {diasDesde(m.preco.conferidoEm) > 90 &&
                                                        ` · há ${diasDesde(m.preco.conferidoEm)} dias`}
                                                </span>
                                            ) : (
                                                <span className="text-amber-700 font-semibold">
                                                    sem preço cadastrado — o custo dele não é calculável
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    setEditandoPreco(editandoPreco === m.id ? null : m.id);
                                                    setEntradaMi(m.preco ? String(m.preco.usdEntradaMi) : '');
                                                    setSaidaMi(m.preco ? String(m.preco.usdSaidaMi) : '');
                                                }}
                                                className="text-xs font-semibold text-slate-500 hover:text-slate-900 underline underline-offset-2 mr-3"
                                            >
                                                {m.preco ? 'preço' : 'cadastrar preço'}
                                            </button>
                                            {m.emUso ? (
                                                <span className="text-xs font-bold text-orange-700">EM USO</span>
                                            ) : (
                                                <button
                                                    onClick={() => salvar({ modelo: m.id })}
                                                    disabled={salvando}
                                                    className="text-xs font-semibold text-slate-600 hover:text-slate-900 underline underline-offset-2 disabled:opacity-40"
                                                >
                                                    escolher
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {modelos.modelos
                                    .filter((m) => m.id === editandoPreco)
                                    .map((m) => (
                                        <tr key={`${m.id}-preco`} className="bg-zinc-50">
                                            <td colSpan={5} className="p-4">
                                                <div className="flex flex-wrap items-end gap-3">
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                                                            US$ por 1M de entrada
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.001"
                                                            min="0"
                                                            value={entradaMi}
                                                            onChange={(e) => setEntradaMi(e.target.value)}
                                                            className="w-36 bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                                                            US$ por 1M de saída
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.001"
                                                            min="0"
                                                            value={saidaMi}
                                                            onChange={(e) => setSaidaMi(e.target.value)}
                                                            className="w-36 bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono"
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={() => void salvarPreco(m.id)}
                                                        disabled={salvando}
                                                        className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-sm text-sm font-bold disabled:opacity-40"
                                                    >
                                                        Salvar preço
                                                    </button>
                                                    <button
                                                        onClick={() => setEditandoPreco(null)}
                                                        className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 pb-2"
                                                    >
                                                        cancelar
                                                    </button>
                                                    <p className="text-xs text-slate-500 basis-full">
                                                        Confira em <code>ai.google.dev/gemini-api/docs/pricing</code>. A data
                                                        de hoje é gravada como “conferido em”, e a tela destaca preço com mais
                                                        de 90 dias.
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/*
                  O modelo em uso pode NÃO estar na lista do provedor — modelo
                  desligado, ou chave que perdeu acesso a ele. Dizer isso é o que
                  transforma "a esteira parou de gerar" numa causa visível.
                */}
                {modelos?.consultouProvedor && !modelos.modelos.some((m) => m.emUso) && (
                    <div className="bg-red-50 border border-red-200 text-red-900 rounded-sm p-4 text-sm flex items-start gap-2">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>
                            O modelo configurado (<code>{config?.modelo}</code>) <strong>não aparece</strong> na
                            lista desta chave. Ele pode ter sido desligado. Escolha um da tabela acima antes do
                            próximo lote.
                        </span>
                    </div>
                )}
            </section>

            {/* ------------------------------------------------------- modo -- */}
            <section className="bg-white border border-zinc-200 rounded-sm p-8 shadow-sm space-y-6">
                <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-zinc-50 rounded-sm border border-zinc-200 flex items-center justify-center shrink-0">
                        <Zap className="text-orange-700" size={22} />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900">Modo da esteira</h3>
                        <p className="text-slate-500 text-sm leading-relaxed mt-1">
                            Vale para o worker — o caminho “Enfileirar para o worker”, que continua rodando com a
                            aba fechada. Em <strong>IA</strong>, cada site é gerado pelo modelo e só cai no
                            template quando a IA não responde.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-zinc-100 pt-6">
                    {(
                        [
                            {
                                v: 'ia' as const,
                                t: 'IA (recomendado)',
                                d: 'O modelo escreve o site. O template é a queda quando a IA falha.',
                            },
                            {
                                v: 'template' as const,
                                t: 'Somente template',
                                d: 'Nunca chama o modelo. Custo zero e resultado determinístico.',
                            },
                        ]
                    ).map((o) => (
                        <button
                            key={o.v}
                            onClick={() => salvar({ modoGeracao: o.v })}
                            disabled={salvando || config?.modoGeracao === o.v}
                            className={`text-left p-5 rounded-sm border transition-all ${
                                config?.modoGeracao === o.v
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-zinc-50 border-zinc-200 hover:border-slate-400'
                            }`}
                        >
                            <div className="font-bold text-sm">{o.t}</div>
                            <div
                                className={`text-xs mt-1 ${
                                    config?.modoGeracao === o.v ? 'text-zinc-300' : 'text-slate-500'
                                }`}
                            >
                                {o.d}
                            </div>
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-zinc-100 pt-6">
                    <div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                            Cota diária de IA
                        </div>
                        {config?.cota ? (
                            <div className="text-sm text-slate-900">
                                {numero(config.cota.usadoHoje)} de {numero(config.cota.limiteDia)} chamadas hoje
                                {config.cota.pausado && (
                                    <span className="ml-2 text-xs font-bold text-red-700">PAUSADA</span>
                                )}
                            </div>
                        ) : (
                            <div className="text-sm text-amber-800">
                                Sem cota definida — a IA roda <strong>sem teto</strong>. Para limitar:{' '}
                                <code className="text-xs">pnpm --filter @nyroh/api definir-cota gemini 5000</code>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">
                            Cotação do dólar (só para exibir em R$)
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                defaultValue={config?.cotacaoDolar}
                                onBlur={(e) => {
                                    const v = parseFloat(e.target.value);
                                    if (Number.isFinite(v) && v > 0 && v !== config?.cotacaoDolar) {
                                        void salvar({ cotacaoDolar: v });
                                    }
                                }}
                                className="w-32 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 text-sm font-mono"
                            />
                            <span className="text-xs text-slate-500 self-center">
                                O provedor cobra em dólar; o real é conversão de tela.
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ custo -- */}
            <section className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <BadgeDollarSign className="text-orange-600" size={20} />
                        Gasto real, por token
                    </h3>
                    <div className="bg-white border border-zinc-200 shadow-sm rounded-sm p-1 flex gap-1">
                        {(Object.keys(ROTULO_PERIODO) as Periodo[]).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPeriodo(p)}
                                className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-all ${
                                    periodo === p
                                        ? 'bg-slate-900 text-white'
                                        : 'text-slate-500 hover:text-slate-800 hover:bg-zinc-50'
                                }`}
                            >
                                {ROTULO_PERIODO[p]}
                            </button>
                        ))}
                    </div>
                </div>

                {resumo && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Cartao rotulo="Gasto no período" valor={brl(resumo.custoBrl)} nota={usd(resumo.custoUsd)} />
                            <Cartao
                                rotulo="Custo médio por site"
                                valor={brl(resumo.custoMedioPorSiteBrl)}
                                nota={`${numero(resumo.sitesAtendidos)} site(s) atendido(s)`}
                            />
                            <Cartao
                                rotulo="Chamadas"
                                valor={numero(resumo.chamadas)}
                                nota={`${numero(resumo.falhas)} falha(s)`}
                            />
                            <Cartao
                                rotulo="Tokens"
                                valor={numero(resumo.tokensTotal)}
                                nota={`${numero(resumo.tokensEntrada)} entrada · ${numero(resumo.tokensSaida)} saída`}
                            />
                        </div>

                        {resumo.chamadasSemPreco > 0 && (
                            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-4 text-sm flex items-start gap-2">
                                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                <span>
                                    <strong>{numero(resumo.chamadasSemPreco)} chamada(s)</strong> usaram um modelo
                                    sem preço cadastrado e <strong>não entram</strong> no total acima. O gasto delas
                                    é real; o que falta é o preço para calculá-lo. Cadastre o preço do modelo na
                                    tabela acima.
                                </span>
                            </div>
                        )}

                        {resumo.porModelo.length > 0 && (
                            <div className="bg-white border border-zinc-200 rounded-sm p-6 shadow-sm">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                                    Por modelo
                                </div>
                                <div className="space-y-2">
                                    {resumo.porModelo.map((m) => (
                                        <div
                                            key={m.modelo}
                                            className="flex items-center justify-between text-sm border-b border-zinc-100 pb-2 last:border-0"
                                        >
                                            <span className="font-mono text-xs text-slate-700">{m.modelo}</span>
                                            <span className="text-slate-500 text-xs">
                                                {numero(m.chamadas)} chamadas · {numero(m.tokensTotal)} tokens
                                            </span>
                                            <span className="font-semibold text-slate-900">{brl(m.custoBrl)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="bg-white border border-zinc-200 rounded-sm shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-zinc-100 text-xs font-bold text-slate-400 uppercase tracking-widest">
                        Custo por site — do mais caro para o mais barato
                    </div>
                    {porSite.length === 0 ? (
                        <div className="px-6 py-10 text-center text-sm text-slate-400">
                            Nenhuma geração com IA no período.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest bg-zinc-50">
                                        <th className="px-6 py-3">Cliente</th>
                                        <th className="px-4 py-3 text-right">Chamadas</th>
                                        <th className="px-4 py-3 text-right">Tokens</th>
                                        <th className="px-4 py-3 text-right">Custo</th>
                                        <th className="px-6 py-3">Observação</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-100">
                                    {porSite.map((s) => (
                                        <tr key={s.clienteId ?? s.clienteNome}>
                                            <td className="px-6 py-3">
                                                <div className="font-semibold text-slate-900">{s.clienteNome}</div>
                                                {s.clienteSlug && (
                                                    <div className="text-xs text-slate-400 font-mono">{s.clienteSlug}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">{numero(s.chamadas)}</td>
                                            <td className="px-4 py-3 text-right font-mono text-xs">
                                                {numero(s.tokensTotal)}
                                            </td>
                                            <td className="px-4 py-3 text-right font-semibold">{brl(s.custoBrl)}</td>
                                            <td className="px-6 py-3 text-xs text-slate-500">
                                                {s.chamadasSemPreco > 0 && (
                                                    <span className="text-amber-700">
                                                        {s.chamadasSemPreco} sem preço ·{' '}
                                                    </span>
                                                )}
                                                {s.falhas > 0 && <span className="text-red-700">{s.falhas} falha(s)</span>}
                                                {s.chamadasSemPreco === 0 && s.falhas === 0 && '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

const Cartao: React.FC<{ rotulo: string; valor: string; nota: string }> = ({ rotulo, valor, nota }) => (
    <div className="bg-white border border-zinc-200 rounded-sm p-6 shadow-sm">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{rotulo}</div>
        <div className="text-2xl font-bold text-slate-900 mt-2 tabular-nums">{valor}</div>
        <div className="text-xs text-slate-400 mt-1">{nota}</div>
    </div>
);
